import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeadersTable } from "@/components/ui/headers-table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  AlertCircle,
  CircleCheck,
  ExternalLink,
  Globe,
  List,
  Loader,
} from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import {
  EndpointSchema,
  FetchEndpointRequestDataSchema,
  FetchEndpointResponseSchema,
} from "../../../../../schemas/codegen";
import { HttpMethod } from "../../../ui/http-method";
import { StatusCode } from "../../../ui/status-code";
import { SourceCode } from "../../SourceCode";
import { GenericTool } from "./GenericTool";
import { ToolError, isBootsmanResult } from "./shared";

const ListEndpointsResultSchema = z.object({
  endpoints: z.array(EndpointSchema),
  appName: z.string(),
});

const FetchEndpointResultSchema = FetchEndpointResponseSchema.extend({
  appName: z.string(),
});

const FetchEndpointHeaderSchema =
  FetchEndpointRequestDataSchema.shape.headers.unwrap().element;

const HttpMethodSchema = z.enum([
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "OPTIONS",
  "HEAD",
]);

interface EndpointToolsProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function EndpointTools({ part }: EndpointToolsProps) {
  const isListEndpoints = part.toolName === "listEndpoints";
  const isFetchEndpoint = part.toolName === "fetchEndpoint";

  if (isListEndpoints) {
    return <ListEndpointsTool part={part} />;
  }

  if (isFetchEndpoint) {
    return <FetchEndpointTool part={part} />;
  }

  return <GenericTool part={part} />;
}

function ListEndpointsTool({
  part,
}: {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}) {
  const isError = part.status === "error";
  const isComplete = part.status === "complete";
  const isPending = part.status === "pending";

  const displayName = isPending
    ? "Listing endpoints"
    : isComplete
      ? "Listed endpoints"
      : "Failed to list endpoints";

  const statusIcon = isPending ? (
    <Loader className="w-3 h-3 animate-spin" />
  ) : isComplete ? (
    <CircleCheck className="w-3 h-3 text-muted-foreground" />
  ) : (
    <AlertCircle className="w-3 h-3 text-warning" />
  );

  const listEndpointsResult = useMemo((): z.infer<
    typeof ListEndpointsResultSchema
  > | null => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      const parseResult = ListEndpointsResultSchema.safeParse(part.result.data);
      return parseResult.success ? parseResult.data : null;
    }
    return null;
  }, [part.status, part.result]);

  const endpoints = listEndpointsResult?.endpoints ?? [];
  const hasEndpoints = endpoints.length > 0;

  if (isError || (!hasEndpoints && isComplete)) {
    return (
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="list-endpoints" className="border rounded-md">
          <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm">
            <div className="flex items-center gap-2 w-full">
              <List className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 text-left min-w-0">
                <span className="truncate">{displayName}</span>
              </div>
              <div className="flex-shrink-0 mr-1">{statusIcon}</div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="px-3 pb-2">
              {part.result ? <ToolError result={part.result} /> : null}
              {!hasEndpoints && !isError && (
                <p className="text-sm text-muted-foreground">
                  No endpoints found in the API specification.
                </p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="list-endpoints" className="border rounded-md">
        <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm">
          <div className="flex items-center gap-2 w-full">
            <List className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
            <div className="flex-1 text-left min-w-0">
              <span className="truncate">{displayName}</span>
              {hasEndpoints && (
                <span className="text-xs text-muted-foreground ml-2">
                  ({endpoints.length} endpoint
                  {endpoints.length !== 1 ? "s" : ""})
                </span>
              )}
            </div>
            <div className="flex-shrink-0 mr-1">{statusIcon}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="px-3 pb-2">
            <div className="space-y-2">
              {endpoints.map((endpoint, index) => (
                <div
                  key={`${endpoint.method}-${endpoint.path}-${index}`}
                  className="font-mono bg-input px-3 py-2 rounded-md"
                >
                  <div className="flex items-center gap-2 w-full">
                    {endpoint.statusCode && (
                      <StatusCode
                        statusCode={endpoint.statusCode}
                        className="text-sm"
                      />
                    )}
                    <HttpMethod method={endpoint.method} />
                    <span className="text-foreground flex-1 text-left truncate text-sm">
                      {endpoint.path}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function FetchEndpointTool({
  part,
}: {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}) {
  const isError = part.status === "error";
  const isComplete = part.status === "complete";
  const isPending = part.status === "pending";

  const argsResult = FetchEndpointRequestDataSchema.safeParse(part.args);
  const args = argsResult.success ? argsResult.data : null;

  const methodResult = HttpMethodSchema.safeParse(args?.method);
  const methodString = methodResult.success ? methodResult.data : "UNKNOWN";
  const pathString = typeof args?.path === "string" ? args.path : "/unknown";

  const displayName = isPending
    ? `Calling ${methodString} ${pathString}`
    : isComplete
      ? `Called ${methodString} ${pathString}`
      : `Failed: ${methodString} ${pathString}`;

  const statusIcon = isPending ? (
    <Loader className="w-3 h-3 animate-spin" />
  ) : isComplete ? (
    <CircleCheck className="w-3 h-3 text-muted-foreground" />
  ) : (
    <AlertCircle className="w-3 h-3 text-warning" />
  );

  const fetchEndpointResult = useMemo((): z.infer<
    typeof FetchEndpointResultSchema
  > | null => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      const parseResult = FetchEndpointResultSchema.safeParse(part.result.data);
      return parseResult.success ? parseResult.data : null;
    }
    return null;
  }, [part.status, part.result]);

  const hasResponse = fetchEndpointResult !== null;

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="fetch-endpoint" className="border rounded-md">
        <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm">
          <div className="flex items-center gap-2 w-full">
            <Globe className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
            <div className="flex-1 text-left min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <span className="truncate">{displayName}</span>
                {hasResponse && fetchEndpointResult?.status && (
                  <>
                    <StatusCode
                      statusCode={fetchEndpointResult.status}
                      className="text-xs"
                    />
                    {fetchEndpointResult.duration && (
                      <span className="text-xs text-muted-foreground">
                        ({fetchEndpointResult.duration}ms)
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 mr-1">{statusIcon}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="px-3 pb-2 space-y-3">
            <div>
              <h4 className="text-sm font-medium mb-2">Request</h4>
              <div className="space-y-2">
                <div className="font-mono bg-input hover:bg-input px-3 py-2 rounded-md">
                  <div className="flex items-center gap-2 w-full">
                    <HttpMethod method={methodString} />
                    <span className="text-foreground flex-1 text-left truncate">
                      {pathString}
                    </span>
                    {fetchEndpointResult?.url && (
                      <a
                        href={fetchEndpointResult.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground flex-shrink-0"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>

                {args?.headers &&
                  Array.isArray(args.headers) &&
                  args.headers.length > 0 && (
                    <HeadersTable
                      size="xs"
                      headers={args.headers
                        .map((h) => FetchEndpointHeaderSchema.safeParse(h))
                        .filter((parseResult) => parseResult.success)
                        .map((parseResult) => {
                          const header = parseResult.success
                            ? parseResult.data
                            : null;
                          return header
                            ? { name: header.name, value: header.value }
                            : null;
                        })
                        .filter(
                          (header): header is { name: string; value: string } =>
                            header !== null,
                        )}
                    />
                  )}

                {args?.body && (
                  <ScrollArea className="max-h-64 max-w-full">
                    <SourceCode
                      content={
                        typeof args.body === "string"
                          ? args.body
                          : JSON.stringify(args.body, null, 2)
                      }
                      className="text-xs"
                      isStreaming={false}
                    />
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                )}
              </div>
            </div>

            {(hasResponse || (isComplete && !!part.result)) && (
              <div>
                <h4 className="text-sm font-medium mb-2">Response</h4>
                <div className="space-y-2">
                  {fetchEndpointResult?.status && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Status:
                      </span>
                      <StatusCode
                        statusCode={fetchEndpointResult.status}
                        className="text-xs"
                      />
                      {fetchEndpointResult.duration && (
                        <span className="text-xs text-muted-foreground">
                          ({fetchEndpointResult.duration}ms)
                        </span>
                      )}
                    </div>
                  )}

                  {fetchEndpointResult?.headers &&
                    Object.keys(fetchEndpointResult.headers).length > 0 && (
                      <HeadersTable
                        size="xs"
                        headers={Object.entries(
                          fetchEndpointResult.headers,
                        ).map(([key, value]) => ({
                          name: key,
                          value: String(value),
                        }))}
                      />
                    )}

                  {fetchEndpointResult?.body && (
                    <ScrollArea className="max-h-64 max-w-full">
                      <SourceCode
                        content={(() => {
                          try {
                            return JSON.stringify(
                              JSON.parse(fetchEndpointResult.body),
                              null,
                              2,
                            );
                          } catch {
                            return fetchEndpointResult.body;
                          }
                        })()}
                        className="text-xs"
                        isStreaming={false}
                      />
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  )}
                </div>
              </div>
            )}

            {isError && part.result ? <ToolError result={part.result} /> : null}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
